import { useEffect, useState } from 'react';
import type { AccesoDePlataforma, FichaDeNegocio, MiembroDePlataforma, SuscripcionDeNegocio } from '../../api/ops/backoffice';
import { ErrorApi } from '../../api/problema';
import { Boton } from '../../componentes/Boton';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { PanelLateral } from '../../componentes/PanelLateral';
import { estadoDeError } from '../../logica/estados';
import { SinPermiso } from '../SinPermiso';
import type { PropsSeccion } from '../tipos';
import { NOTA_ACCESOS, NOTA_EQUIPO, PESTANAS, lineaDeFicha, lineasAccesos, lineasEquipo, lineasPlan, lineasResumen, pestanaDe, type Linea, type Pestana } from './logica';
import { Ventanas, type TipoVentana } from './Ventanas';

/**
 * **Ficha de un negocio** en el backoffice (1.20, G138): RV Panel `ficha` con la botonera Resumen · Plan · Equipo ·
 * Accesos, sin entrar en el negocio. Resumen (`getPlatformBusiness`), Plan (`getPlatformBusinessSubscription`), Equipo
 * (`listPlatformBusinessMembers`, que **queda en Auditoría y en la Actividad del negocio**: solo se pide al abrir la
 * pestaña) y Accesos (`listPlatformBusinessAccesses`). «Entrar al negocio» en la cabecera (no en Equipo). Herramientas del
 * Superadmin: en Plan, «Ampliar la prueba» (solo en prueba) y «Aplicar un cupón»; en Resumen, «Atribuir a un partner» o
 * «Cambiar de partner» y «Quitar el partner». La pestaña va en `?pestana=`. En Resumen, «Ver su auditoría» abre la
 * auditoría de plataforma ya filtrada por este negocio (`?seccion=auditoria&negocio=`, G1173).
 */
interface Props extends PropsSeccion {
    id: string;
    onCerrar: () => void;
    onCambiado: () => void;
}

interface Estado<T> {
    datos: T | null;
    error: unknown;
}

/** Pide una vez (por `clave`) lo que necesita la pestaña; null en `pedir` no pide nada. */
function usePedir<T>(clave: string, pedir: (() => Promise<T>) | null, onCaducada: () => void): Estado<T> {
    const [estado, setEstado] = useState<Estado<T> & { clave: string | null }>({ clave: null, datos: null, error: null });
    const activo = pedir !== null;
    useEffect(() => {
        if (pedir === null) {
            return;
        }
        let vivo = true;
        pedir()
            .then((datos) => {
                if (vivo) {
                    setEstado({ clave, datos, error: null });
                }
            })
            .catch((error: unknown) => {
                if (!vivo) {
                    return;
                }
                if (error instanceof ErrorApi && error.estado === 401) {
                    onCaducada();
                    return;
                }
                setEstado({ clave, datos: null, error });
            });
        return () => {
            vivo = false;
        };
        // `pedir` cambia en cada pintado; la petición la marca `clave`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clave, activo, onCaducada]);
    return estado.clave === clave ? estado : { datos: null, error: null };
}

export function Ficha(p: Props) {
    const { ops, id, movil, superadmin, busqueda, cambiarConsulta, onSesionCaducada, onCerrar, onCambiado, onAccesoAbierto } = p;
    const pestana = pestanaDe(busqueda.get('pestana'));
    const [vuelta, setVuelta] = useState(0);
    const [ventana, setVentana] = useState<TipoVentana | null>(null);
    const ficha = usePedir<FichaDeNegocio>(`ficha:${id}:${String(vuelta)}`, () => ops.getPlatformBusiness(id), onSesionCaducada);
    // La suscripción se pide para Plan y para el texto de «Ampliar la prueba».
    const plan = usePedir<SuscripcionDeNegocio>(`plan:${id}:${String(vuelta)}`, pestana === 'plan' ? () => ops.getPlatformBusinessSubscription(id) : null, onSesionCaducada);
    // Ver el equipo deja línea en Auditoría y en la Actividad del negocio (G138): solo al abrir la pestaña.
    const equipo = usePedir<{ data: MiembroDePlataforma[] }>(`equipo:${id}`, pestana === 'equipo' ? () => ops.listPlatformBusinessMembers(id, { limit: 100 }) : null, onSesionCaducada);
    const accesos = usePedir<{ data: AccesoDePlataforma[] }>(`accesos:${id}:${String(vuelta)}`, pestana === 'accesos' ? () => ops.listPlatformBusinessAccesses(id, { limit: 100 }) : null, onSesionCaducada);

    const f = ficha.datos;
    const propietario = f?.owner_name ?? f?.owner_email ?? 'el propietario';
    const ir = (clave: Pestana) => {
        cambiarConsulta({ pestana: clave === 'resumen' ? null : clave }, true);
    };
    const hecho = () => {
        setVentana(null);
        setVuelta((v) => v + 1);
        onCambiado();
    };

    const cuerpo = ((): { lineas: Linea[] | null; error: unknown } => {
        switch (pestana) {
            case 'plan':
                return { lineas: plan.datos === null || f === null ? null : lineasPlan(plan.datos, f), error: plan.error };
            case 'equipo':
                return { lineas: equipo.datos === null ? null : lineasEquipo(equipo.datos.data), error: equipo.error };
            case 'accesos':
                return { lineas: accesos.datos === null ? null : lineasAccesos(accesos.datos.data, new Date()), error: accesos.error };
            default:
                return { lineas: f === null ? null : lineasResumen(f), error: null };
        }
    })();

    const herramientas: { clave: TipoVentana; texto: string; icono: NombreIcono }[] = [];
    if (superadmin && f !== null && pestana === 'plan') {
        if (f.subscription_status === 'trialing') {
            herramientas.push({ clave: 'prueba', texto: 'Ampliar la prueba', icono: 'calendar-plus' });
        }
        herramientas.push({ clave: 'cupon', texto: 'Aplicar un cupón', icono: 'percent' });
    }
    if (superadmin && f !== null && pestana === 'resumen') {
        herramientas.push({ clave: 'partner', texto: f.partner_name === null ? 'Atribuir a un partner' : 'Cambiar de partner', icono: 'handshake' });
        if (f.partner_name !== null) {
            herramientas.push({ clave: 'quitar-partner', texto: 'Quitar el partner', icono: 'unlink' });
        }
    }
    const nota = pestana === 'equipo' ? NOTA_EQUIPO : pestana === 'accesos' ? NOTA_ACCESOS : null;
    const etiqueta = PESTANAS.find((x) => x.clave === pestana)?.etiqueta ?? '';

    const contenido = (() => {
        const error = ficha.error ?? cuerpo.error;
        if (error !== null) {
            if (estadoDeError(error).tipo === 'sin-permiso') {
                return <SinPermiso seccion={etiqueta === 'Resumen' ? 'este negocio' : etiqueta} superadmin={superadmin} movil={movil} onIr={null} />;
            }
            return (
                <EstadoError
                    seccion={etiqueta}
                    codigoSoporte={estadoDeError(error).codigoSoporte}
                    movil={movil}
                    onReintentar={() => {
                        setVuelta((v) => v + 1);
                    }}
                />
            );
        }
        if (cuerpo.lineas === null) {
            return <EstadoCargando seccion={etiqueta} />;
        }
        return (
            <>
                <div className="rv-bo-lineas">
                    {cuerpo.lineas.map((l, i) => (
                        <div key={`${l.clave}-${String(i)}`} className={`rv-bo-linea${l.fuerte ? ' rv-bo-linea--fuerte' : ''}`}>
                            <span className="rv-bo-linea__clave">
                                <span>{l.clave}</span>
                                {l.sub !== null && <span className="rv-bo-linea__sub">{l.sub}</span>}
                            </span>
                            <span className="rv-bo-linea__valor">{l.valor}</span>
                        </div>
                    ))}
                </div>
                {herramientas.length > 0 && (
                    <div className="rv-bo-herr-ficha">
                        {herramientas.map((h) => (
                            <Boton
                                key={h.clave}
                                tipo="secundario"
                                icono={h.icono}
                                movil={movil}
                                onClick={() => {
                                    setVentana(h.clave);
                                }}
                            >
                                {h.texto}
                            </Boton>
                        ))}
                    </div>
                )}
                {superadmin && f !== null && pestana === 'resumen' && (
                    // Provisional hasta C.75 (G1173): un botón secundario más, hasta que diseño dibuje el enlace. Solo el
                    // Superadmin (G1198): Soporte solo ve la auditoría del negocio en el que tiene un acceso abierto (G1138).
                    <div className="rv-bo-herr-ficha">
                        <Boton
                            tipo="secundario"
                            icono="history"
                            movil={movil}
                            onClick={() => {
                                cambiarConsulta({ seccion: 'auditoria', negocio: id, pestana: null, ventana: null });
                            }}
                        >
                            Ver su auditoría
                        </Boton>
                    </div>
                )}
                {nota !== null && (
                    <div className="rv-bo-nota">
                        <Icono nombre="lock" />
                        <span>{nota}</span>
                    </div>
                )}
            </>
        );
    })();

    return (
        <>
            <PanelLateral
                abierto
                onCerrar={onCerrar}
                movil={movil}
                tamano="ficha"
                eyebrow="Negocio"
                titulo={f?.name ?? ''}
                subtitulo={f === null ? undefined : lineaDeFicha(f)}
                accion={f !== null && pestana !== 'equipo' ? 'Entrar al negocio' : ''}
                accionIcono="headset"
                onAccion={() => {
                    setVentana('entrar');
                }}
            >
                <div className="rv-bo-ficha">
                    <div className="rv-bo-botonera" role="tablist" aria-label="Apartados de la ficha">
                        {PESTANAS.map((x) => (
                            <button
                                key={x.clave}
                                type="button"
                                role="tab"
                                aria-selected={pestana === x.clave}
                                className="rv-bo-botonera__b"
                                onClick={() => {
                                    ir(x.clave);
                                }}
                            >
                                <Icono nombre={x.icono} />
                                <span>{x.etiqueta}</span>
                            </button>
                        ))}
                    </div>
                    <div className="rv-bo-ficha__contenido">
                        <h3 className="rv-bo-ceja">{etiqueta}</h3>
                        {contenido}
                    </div>
                </div>
            </PanelLateral>
            {f !== null && (
                <Ventanas
                    tipo={ventana}
                    ops={ops}
                    ficha={f}
                    movil={movil}
                    superadmin={superadmin}
                    propietario={propietario}
                    finPrueba={plan.datos?.trial_ends_at ?? null}
                    onCerrar={() => {
                        setVentana(null);
                    }}
                    onHecho={hecho}
                    onAccesoAbierto={onAccesoAbierto}
                />
            )}
        </>
    );
}
